<template>
    <div class="account-details-addresses-tab tab-contents">
        <NoResults v-if="totalItems < 1"
                   icon="fa-solid fa-location-dot-slash"
                   color="light"
                   lang="MANAGE_ACCOUNT.NO_ADDRESSES">
            <!-- Add address button -->
            <SquareBtn color="info"
                       class="mt-1"
                       @click="addAddress">
                <i class="fa-solid fa-plus"></i>
                {{ l('ADD') }}
            </SquareBtn>
        </NoResults>
        <template v-else>
            <AddressList :addresses="account.addresses"
                         :changing-primary="setAddressAsPrimaryInProgress"
                         @set-primary="setAddressAsPrimary"
                         @edit="editAddress"
                         @delete="deleteAddress">
                <!-- Add address button -->
                <SquareBtn color="info"
                           @click="addAddress">
                    <i class="fa-solid fa-plus"></i>
                    {{ l('ADD') }}
                </SquareBtn>
            </AddressList>
        </template>
    </div>
</template>

<script>
import Address from '@/lib/Addresses/Address';
import AddressList from '@/epos/vue/components/Customers/AddressList';
import NoResults from '@/vue/components/NoResults';
import SquareBtn from '@/vue/components/SquareBtn';
import { cloneDeep } from '@/functions/clone';
import { container } from '@/container';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';

export default {
    components: { NoResults, AddressList, SquareBtn },

    props: {
        /**
         * @type {CustomerAccount}
         */
        account: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            setAddressAsPrimaryInProgress: false,
        };
    },

    computed: {
        /**
         * @returns {Address[]}
         */
        addresses() {
            return this.account.addresses;
        },

        totalItems() {
            return this.addresses.length;
        },
    },

    methods: {
        async addAddress() {
            let address = new Address();
            address.country = container.userService.currentBranch.country;

            try {
                const result = await container.vueModalFactory.open(
                    MODAL_NAMES__EPOS.ADDRESS,
                    {
                        address,
                    }
                );
                await this.addAddressToCurrentAccount(result.address);
            } catch (error) {
                // Do nothing - they just closed the modal.
            }
        },

        /**
         * Persist a new address for the currently displayed account.
         *
         * @param {Address} address
         *
         * @return {Promise<Address>}
         */
        async addAddressToCurrentAccount(address) {
            let addPromise = container.savedAddressManager.addAddressToAccount(address, this.account);
            container.toastFactory.spinner(
                'MANAGE_ACCOUNT.ADDRESS_ADDING',
                address.getFormattedAddress(),
                addPromise,
            );

            try {
                const newAddress = await addPromise;

                container.toastFactory.success(
                    'MANAGE_ACCOUNT.ADDRESS_ADDED',
                    newAddress.getFormattedAddress(),
                );

                return newAddress;
            } catch (error) {
                container.toastFactory.error(
                    'MANAGE_ACCOUNT.ADDRESS_FAILED',
                    error.message,
                );

                throw error;
            }
        },

        /**
         * @param {Address} address
         *
         * @returns {Promise}
         */
        async setAddressAsPrimary(address) {
            // Block multiple clicks.
            if (this.setAddressAsPrimaryInProgress) {
                return;
            }

            this.setAddressAsPrimaryInProgress = true;

            try {
                await container.savedAddressManager.setAddressAsPrimaryOnAccount(address, this.account);

                // Set the default address in the UI.
                this.addresses.forEach((savedAddress) => {
                    savedAddress.defaultBilling = savedAddress.savedAddressID === address.savedAddressID;
                });

                this.account.refreshPrimaryAddress();
            } catch (e) {
                container.toastFactory.error(
                    'MANAGE_ACCOUNT.ADDRESS_UPDATE_FAILED',
                    e.message,
                );
            } finally {
                this.setAddressAsPrimaryInProgress = false;
            }
        },

        /**
         * @param {Address} address
         *
         * @returns {Promise}
         */
        async editAddress(address) {
            let addressCopy = cloneDeep(address);
            try {
                const result = await container.vueModalFactory.open(
                    MODAL_NAMES__EPOS.ADDRESS,
                    {
                        address: addressCopy,
                    }
                );

                // Update the original address object's properties.
                Object.assign(address, result.address);

                await this.saveEditedAddress(result.address);
            } catch (error) {
                // Do nothing - they just closed the modal.
            }
        },

        /**
         * @param {Address} address
         *
         * @returns {Promise}
         */
        async saveEditedAddress(address) {
            let updatePromise = container.savedAddressManager.updateAddressOnAccount(address, this.account);
            container.toastFactory.spinner(
                'MANAGE_ACCOUNT.ADDRESS_UPDATING',
                address.getFormattedAddress(),
                updatePromise
            );

            try {
                await updatePromise;
                container.toastFactory.success(
                    'MANAGE_ACCOUNT.ADDRESS_UPDATED',
                    address.getFormattedAddress()
                );
            } catch (error) {
                container.toastFactory.error(
                    'MANAGE_ACCOUNT.ADDRESS_UPDATE_FAILED',
                    error
                );
            }
        },

        /**
         * @param {Address} address
         *
         * @returns {Promise}
         */
        async deleteAddress(address) {
            const wasPrimaryAddress = address.isPrimary();

            let deletePromise = container.savedAddressManager.removeAddressFromAccount(address, this.account);
            container.toastFactory.spinner(
                'MANAGE_ACCOUNT.ADDRESS_DELETING',
                address.getFormattedAddress(),
                deletePromise
            );

            try {
                await deletePromise;

                // Show a toast confirming the address was deleted and allowing the user to undo.
                // The undo button actually re-adds the address as a new one with the same details.
                container.toastFactory.warning(
                    'MANAGE_ACCOUNT.ADDRESS_DELETED',
                    address.getFormattedAddress() + '<br/><a class="btn btn-default btn-sm mt-1">Undo</a>',
                    {
                        // Re-add the address to the account if undo is clicked.
                        onclick: () => this.restoreDeletedAddress(address, wasPrimaryAddress),
                    }
                );
            } catch (error) {
                container.toastFactory.error(
                    'MANAGE_ACCOUNT.ADDRESS_DELETE_FAILED',
                    error.message
                );
            }
        },

        /**
         * @param {Address} address
         * @param {boolean} wasPrimaryAddress
         * @returns {Promise<void>}
         */
        async restoreDeletedAddress(address, wasPrimaryAddress) {
            const newAddress = await this.addAddressToCurrentAccount(address);

            if (wasPrimaryAddress) {
                // If the address that was deleted was the primary address,
                // and the undo button was pressed to re-add the address,
                // we should also mark it as the primary again.
                await this.setAddressAsPrimary(newAddress);
            }
        },
    },
};
</script>
